import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { ViewModeItem } from "../models/ViewModeItem";
import { ViewModeItems } from "../models/ViewModeItem";
import { SortItem, AvailableSortItems } from "../models/SortItem";
import { LookupItem } from "../models/LookupItem";
import { FacetResult } from "../models/FacetResult";
import { ChapterArticle } from "../models/ChapterArticle";
import { SuggestItem } from "../models/SuggestItem";
import { SuggestFilter } from '../models/common/SuggestFilter';
import { SearchEventTypes } from "../models/SearchEventTypes";

export interface ChapterEventsState {
  viewMode: ViewModeItem;
  orderBy: SortItem;
  keywords: string;
  suggestions: SuggestItem[];
  currentEventType: string;
  selectedCategory: undefined | LookupItem;
  selectedChapter: undefined | LookupItem;
  //selectedYearMonth: undefined | LookupItem;
  isLoading: boolean;
  isSuggestLoading: boolean;
  canLoadMore: boolean;
  items: ChapterArticle[];
  pageSize: number;
  page: number;
  initDone: boolean;
}

const initialState: ChapterEventsState = {
  viewMode: ViewModeItems[0],
  orderBy: AvailableSortItems[1],
  keywords: "",
  suggestions: [],
  currentEventType: SearchEventTypes.UpcomingEvents,
  selectedCategory: undefined,
  selectedChapter: undefined,
  //selectedYearMonth: undefined,
  isLoading: false,
  isSuggestLoading: false,
  canLoadMore: false,
  items: [],
  pageSize: 9,
  page: 1,
  initDone: false,
};

export type SearchFilter = {
  keywords?: string;
  searchEventType?: string;
  category?: string;
  chapter?: string;
  yearMonth?: string;
  sortBy?: string;
  sortAsc?: boolean;
  path?: string;
  page: number;
  pageSize: number;
};

export const searchChapterEventsSuggest = createAsyncThunk<SuggestItem[], SuggestFilter>(
  "data/searchChapterEventsSuggest",
  async (payload: SuggestFilter): Promise<SuggestItem[]> => {
    const response = await fetch("/chapterssearch/suggest", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error("Failed to fetch data");
    }
    return (await response.json()) as SuggestItem[];
  }
);

export const searchChapterEvents = createAsyncThunk<
  FacetResult<ChapterArticle>,
  SearchFilter
>(
  "data/searchChapterEvents",
  async (payload: SearchFilter): Promise<FacetResult<ChapterArticle>> => {
    const response = await fetch("/chapterssearch", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error("Failed to fetch data");
    }
    return (await response.json()) as FacetResult<ChapterArticle>;
  }
);

export const chapterEventsSlice = createSlice({
  name: "chapterEvents",
  initialState,
  reducers: {
    changePage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    changeViewMode: (state, action: PayloadAction<ViewModeItem>) => {
      state.viewMode = action.payload;
    },
    changeKeywords: (state, action: PayloadAction<string>) => {
      if (state.keywords!== action.payload)
      {
        state.items = [];
        state.canLoadMore = false;
      }
      state.keywords = action.payload;
      state.page = 1;
    },
    changeEventType: (state, action: PayloadAction<string>) => {
      if (state.currentEventType !== action.payload) {
        state.items = [];
        state.canLoadMore = false;
      }
      state.currentEventType = action.payload;
      state.orderBy = action.payload === SearchEventTypes.UpcomingEvents ? AvailableSortItems[1] : AvailableSortItems[2];
      state.page = 1;
    },
    changeCategory: (state, action: PayloadAction<undefined | LookupItem>) => {
      state.items = [];
      state.selectedCategory = action.payload;
      state.page = 1;
      state.canLoadMore = false;
    },
    changeChapter: (
      state,
      action: PayloadAction<undefined | LookupItem>
    ) => {
      state.items = [];
      state.selectedChapter = action.payload;
      state.page = 1;
      state.canLoadMore = false;
    },
    // changeYearMonth: (
    //   state,
    //   action: PayloadAction<undefined | LookupItem>
    // ) => {
    //   state.items = [];
    //   state.selectedYearMonth = action.payload;
    //   state.page = 1;
    //   state.canLoadMore = false;
    // },
    changeInit: (state, action: PayloadAction<boolean>) => {
      state.initDone = action.payload;
    },
    clearSuggestions: (state) => {
      state.suggestions = [];
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchChapterEvents.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
        searchChapterEvents.fulfilled,
        (state, action: PayloadAction<FacetResult<ChapterArticle>>) => {
          state.isLoading = false;

          if (action.payload) {
            if (state.page == 1) {
              state.items = [];
            }
            if (action.payload.hits.length > 0) {
              state.items.push(...action.payload.hits);
            }

            state.canLoadMore =
              action.payload.totalHits > state.items.length &&
              state.items.length > 0;
          }
        }
      )
      .addCase(searchChapterEvents.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(searchChapterEventsSuggest.pending, (state) => {
        state.isSuggestLoading = true;
      })
      .addCase(
        searchChapterEventsSuggest.fulfilled,
        (state, action: PayloadAction<SuggestItem[]>) => {
          state.isSuggestLoading = false;

          state.suggestions = [];

          if (action.payload.length > 0) {
            state.suggestions = [...action.payload];
          }
        }
      )
      .addCase(searchChapterEventsSuggest.rejected, (state) => {
        state.isSuggestLoading = false;
      });
  },
});

export const {
  changePage,
  changeViewMode,
  changeKeywords,
  changeCategory,
  changeChapter,
  changeYearMonth,
  changeInit,
  clearSuggestions,
  changeEventType
} = chapterEventsSlice.actions;

export default chapterEventsSlice.reducer;
